import { useEffect } from "react";
import { AuthShell, CardThemeToggle } from "./AuthShell";
import { NetIndicator } from "./NetIndicator";
import { spaNavigate } from "../../utils/navigation";
import { setTokens } from "../../utils/jwt-storage";
import { refreshAuthHeaders } from "../../providers/ApiProvider";

/**
 * React port of ``users/templates/users/new-ui/verify_done.html``.
 *
 * Landing after the user clicked the verification link in their inbox.
 * Django's ``verify_email_view`` (users/verification_views.py) has
 * already toggled ``is_email_verified=True`` server-side and 302'd
 * here with a JWT pair in the URL fragment
 * (``/auth/verify-done#access=…&refresh=…``). We pluck it, stash in
 * localStorage, and land the user on home.
 *
 * URL fragment is used instead of a query string because fragments
 * are never sent to the server — the token pair only exists client-
 * side. Also strips the fragment from the URL bar via
 * ``replaceState`` so no back / share / screenshot leaks the tokens.
 */
export const VerifyDone = () => {
  useEffect(() => {
    // Try to pluck JWT from ``window.location.hash`` — the redirect
    // from Django's verify_email_view carries ``access=…&refresh=…``
    // there. Best-effort: if the hash is empty (already-verified
    // repeat click, or reached this route without a token), we just
    // route to home and let the API's 401 handler bounce back to
    // /auth/login if the user isn't authenticated.
    try {
      const hash = (window.location.hash || "").replace(/^#/, "");
      if (hash) {
        const params = new URLSearchParams(hash);
        const access = params.get("access");
        const refresh = params.get("refresh");
        if (access) {
          setTokens(access, refresh ?? null);
          refreshAuthHeaders();
        }
        // Strip the fragment from the URL bar so the tokens don't
        // appear in shared / screenshotted URLs.
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch {
      /* private browsing / disabled localStorage */
    }

    const t = window.setTimeout(() => spaNavigate("/", true), 1200);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <AuthShell>
      <div className="form-wrapper">
        <div className="form-wrapper-header">
          <h2>Email verified</h2>
          <CardThemeToggle />
          <NetIndicator />
        </div>
        <p style={{ color: "var(--color-neutral-content-subtler, #4b5563)" }}>
          Your account is active. Taking you into AfriAnnotate…
        </p>
        <button type="button" className="login-button" onClick={() => spaNavigate("/", true)}>
          Continue
        </button>
      </div>
    </AuthShell>
  );
};

export default VerifyDone;
